// ZUSTAND→ATOM off-ramp (docs/projects/complete/web/zustand-to-effect-atom-migration.md)
// Status: not-started
// Target: Atom.make + Atom.fn actions on the app runtime
import { readyAudioStatuses } from '@listening/api-contract/audio-statuses';
import type { AudioItemSummary } from '@listening/api-contract/legacy-client-types';
import type { DocumentType } from '@listening/api-contract/schemas';
import { ApiClient } from '@listening/api-contract/api-client-tag';
import {
  type AudioItem,
  AudioItems,
  audioItemsBaseKey,
} from '@listening/frontend/query/audio-items';
import {
  flattenAudioPages,
  invalidateAudioCaches,
  continueListeningQueryKey,
  libraryListInfiniteArgs,
  libraryListQueryKey,
  useAudioWalkLifecycle,
  useContinueListening,
} from '@listening/frontend/query/audio-list';
import { getLibrarySortMode } from '@listening/frontend/stores/library-sort-store';
import { pinContinueListening } from '@/components/web-fe/design-v2/library-sort';
import {
  useUnarchiveAudioMutation,
  useUpdateAudioMutation,
} from '@listening/frontend/query/audio-mutations';
import { Effect, Schema, SchemaTransformation, Struct } from 'effect';
import { eq, runApiPromise } from '@/runtimes/effect-query';
import { formatTimeSeconds } from '@listening/utils/ts-utils';

import { useAudioPrefsStore } from '@listening/frontend/stores/audio-preferences-store';
import {
  type InfiniteData,
  type QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useMatchRoute, useNavigate } from '@tanstack/react-router';
import { throttle } from 'throttle-debounce';
import { useAtomSet, useAtomValue } from '@effect/atom-react';
import { atomKvsSync } from '@listening/frontend/atoms/kvs';
import { AudioConversionSummary } from '@listening/api-contract/schemas';
import {
  useAudioChunkPlayer,
  useAudioChunkSelector,
  useDocumentSummary,
} from '@/lib/audio-chunk-player';
import { useAudioDetailedView } from '@listening/frontend/query/audio-detail';
import { rootLogger } from '@/lib/root-logger';
import { useAuthSession } from '@/services/auth-service';
import { useFreePlanPrivateAudioLimit } from './subscription-service';
import { clientEnv } from '@/env';
import { useConversionPipelinePreferenceStore } from '@/stores/conversion-pipeline-preference';

const moduleLogger = rootLogger.child({
  module: 'AudioService',
});

export type CreateAudioInput = {
  documentUrl: string;
  documentType?: DocumentType;
  title?: string;
  folderId?: string | null;
};

function normalizeCreateAudioInput(input: string | CreateAudioInput) {
  return typeof input === 'string' ? { documentUrl: input } : input;
}

const useCurrentAudio = () => {
  const audioId = useAudioChunkSelector((s) => s.audioId);
  const query = useAudioDetailedView(audioId);
  return { query, audioId };
};

const useLoadAudioFromProgress = () =>
  useAudioChunkPlayer().loadAudioFromProgress;

function processingItemStatusString(audio: AudioItemSummary) {
  if (audio.conversion_status === 'initializing') return 'Initializing';
  if (audio.conversion_status === 'completedExtraction')
    return 'Generating Audio';
  if (audio.conversion_status === 'complete') return 'Completed';
  if (audio.conversion_status === 'deleted') return 'Deleted';
  if (audio.conversion_status === 'processingPDF') return 'Processing PDF';
  // if (audio.conversion_status === 'Error')
  return audio.error ?? 'An error occurred';
  // moduleLogger.warn(
  //   { conversion_status: audio.conversion_status },
  //   'Unknown audio conversion status',
  // );
  // return '';
}

const useContinueListeningAudioItems = (): ReadonlyArray<AudioItemSummary> => {
  // Ordering/filtering logic lives in `selectContinueListening` (shared query
  // layer) so web / RN / browser-ext derive it identically — see that helper
  // for the legacy-parity rationale (played_at = `progress.updated_at_ms`).
  //
  // Non-suspense + auth-gated: PlayBar mounts on the public `/doc/$docId` SSR
  // route, where the user is unauthenticated and the list endpoint 401s.
  // Suspense would block hydration (and spam Unauthorized errors). On `/app/*`
  // the home loader prefetches the continue-listening query, so the non-suspense
  // read resolves warm; the player just lists nothing for signed-out visitors.
  const { user } = useAuthSession();
  const query = useContinueListening({ enabled: !!user });
  return query.data ?? [];
};

// Rows go through the FULL contract schema (not typeSchema): its encoded
// side is the lenient one — rows persisted before a later-added optionalWith
// field still decode, instead of one old row fail-opening the whole cache.
// The transform's target is the TYPE-side identity so rows are
// decoded/encoded exactly once, by the union members.
const sessionConversionHistoryStateSchema = Schema.Struct({
  history: Schema.Array(AudioConversionSummary),
});
type SessionConversionHistoryState =
  typeof sessionConversionHistoryStateSchema.Type;
const emptySessionConversionHistory: SessionConversionHistoryState = {
  history: [],
};

// zustand-persist wrote a `{state, version}` envelope; decode merges over the
// empty default, encode writes the bare shape. See plan doc §C2.
const partialSessionConversionHistorySchema =
  sessionConversionHistoryStateSchema.mapFields(Struct.map(Schema.optional));
const sessionConversionHistoryAtom = atomKvsSync({
  key: 'session-conversion-history',
  schema: Schema.Union([
    Schema.Struct({
      state: partialSessionConversionHistorySchema,
      version: Schema.Int,
    }),
    partialSessionConversionHistorySchema,
  ]).pipe(
    Schema.decodeTo(
      Schema.toType(sessionConversionHistoryStateSchema),
      SchemaTransformation.transform({
        decode: (stored) => ({
          ...emptySessionConversionHistory,
          ...('state' in stored ? stored.state : stored),
        }),
        encode: (state) => state,
      }),
    ),
  ),
  defaultValue: () => emptySessionConversionHistory,
});

const useSessionConversionHistoryStore = () => {
  const history = useAtomValue(sessionConversionHistoryAtom).history;
  const setState = useAtomSet(sessionConversionHistoryAtom);
  return {
    history,
    setHistory: (history: ReadonlyArray<AudioItemSummary>) => {
      setState({ history });
    },
  };
};

const useCreateAudioMutation = () => {
  const { setHistory, history } = useSessionConversionHistoryStore();
  const { limitEnabled } = useFreePlanPrivateAudioLimit();
  const queryClient = useQueryClient();
  const updateAudioMutation = audioService.mutations.useUpdateAudioMutation();
  // Dev/staging-only pipeline override; never sent in production (flag off there).
  const pipelinePreference = useConversionPipelinePreferenceStore(
    (s) => s.preference,
  );
  const conversionPipelineVersion =
    clientEnv.conversionPipelineToggleEnabled &&
    pipelinePreference !== 'default'
      ? pipelinePreference
      : undefined;
  // Dev/staging-only vision override; only sent when the flag is on AND the user forced a value
  // ('on'/'off'). 'default' (or prod, where the flag is off) sends undefined so the server config
  // decides — v2 defaults vision off. 'on' sends false to force vision back on for a v2 test.
  const visionPreference = useConversionPipelinePreferenceStore(
    (s) => s.vision,
  );
  const conversionVisionOff =
    !clientEnv.conversionPipelineToggleEnabled || visionPreference === 'default'
      ? undefined
      : visionPreference === 'off';
  // Dev/staging-only v2 variant switches, same semantics: only sent when the flag is on AND the
  // user forced a non-default value. All are no-ops on a v1 conversion.
  const ocrProviderPreference = useConversionPipelinePreferenceStore(
    (s) => s.ocrProvider,
  );
  const conversionOcrProvider =
    clientEnv.conversionPipelineToggleEnabled &&
    ocrProviderPreference !== 'default'
      ? ocrProviderPreference
      : undefined;
  const labelModelPreference = useConversionPipelinePreferenceStore(
    (s) => s.labelModel,
  );
  const conversionLabelModel =
    clientEnv.conversionPipelineToggleEnabled &&
    labelModelPreference !== 'default'
      ? labelModelPreference
      : undefined;
  const updateModePreference = useConversionPipelinePreferenceStore(
    (s) => s.updateMode,
  );
  const conversionUpdateMode =
    clientEnv.conversionPipelineToggleEnabled &&
    updateModePreference !== 'default'
      ? updateModePreference
      : undefined;
  return useMutation({
    mutationFn: async (input: string | CreateAudioInput) => {
      const createAudioInput = normalizeCreateAudioInput(input);

      // No optimistic insert: the server assigns the id. Once the API responds,
      // invalidate every audio cache so the in-progress audio shows up in the
      // bounded lists (the server decides which list it belongs to).
      const audio_conversion = await runApiPromise(
        AudioItems.createByUrlEffect({
          document_url: createAudioInput.documentUrl,
          document_type: createAudioInput.documentType,
          title: createAudioInput.title,
          folder_id: createAudioInput.folderId,
          conversion_pipeline_version: conversionPipelineVersion,
          conversion_vision_off: conversionVisionOff,
          conversion_ocr_provider: conversionOcrProvider,
          conversion_label_model: conversionLabelModel,
          conversion_update_mode: conversionUpdateMode,
        }),
      );
      await invalidateAudioCaches(queryClient);

      // Free-plan side-effect: new audios default to public so they count
      // against the free public-audio quota rather than the paid private one.
      if (limitEnabled) {
        try {
          const updated = await updateAudioMutation.mutateAsync({
            audioId: audio_conversion.id,
            updates: { visibility: 'public' },
          });
          return { audio_conversion: updated };
        } catch (error) {
          moduleLogger.warn(
            { error },
            'Failed to make newly-created audio public',
          );
        }
      }

      return { audio_conversion };
    },
    onSuccess: (result, input) => {
      const { documentUrl } = normalizeCreateAudioInput(input);
      moduleLogger.info({ documentUrl }, 'Successfully requested convert url');
      setHistory([...history, result.audio_conversion]);
    },
  });
};

const useDeleteAudioMutation = () => {
  const navigate = useNavigate();
  const matchRoute = useMatchIsReadAlongScreenActiveForAudio();

  const { unloadAudio } = useAudioChunkPlayer();
  const loadedAudioId = useAudioChunkSelector((s) => s.audioId);

  // Optimistically remove the row from every audio cache via the walk; the
  // server DELETE persists it; rollback on failure restores the snapshot.
  return useMutation({
    ...eq.mutationOptions({
      mutationKey: [...audioItemsBaseKey, 'delete'],
      mutationFn: (audioItem: AudioItemSummary) =>
        AudioItems.deleteEffect(audioItem.id),
    }),
    ...useAudioWalkLifecycle<AudioItemSummary>((audioItem) => ({
      kind: 'removeById',
      id: audioItem.id,
    })),
    onSuccess: (_resp, audioItem) => {
      moduleLogger.info('Deleted audio item successfully');
      if (loadedAudioId === audioItem.id) {
        unloadAudio();
      }
      if (matchRoute(audioItem)) {
        moduleLogger.info('Navigating to home screen from deleted audio');
        return navigate({ to: '/app/home' });
      }
    },
  });
};

const useMatchIsReadAlongScreenActiveForAudio = () => {
  const matchRoute = useMatchRoute();
  return (opts: {
    id: string;
    title?: string | null;
    url_slug?: string | null;
  }) => {
    const matchReadAlongRoute = (val: string) =>
      matchRoute({
        to: '/app/readalong/$audioIdOrSlug',
        params: { audioIdOrSlug: val },
        fuzzy: true,
      }) as boolean;

    if (opts.url_slug) {
      if (matchReadAlongRoute(opts.url_slug)) return true;
      // Also try the publicId suffix alone for backwards compat
      const publicId = opts.url_slug.split('-').at(-1);
      if (publicId && matchReadAlongRoute(publicId)) return true;
    }
    return matchReadAlongRoute(opts.id);
  };
};

const postAudioProgressThrottled = throttle(
  15_000,
  async ({
    audio_id,
    chapter_id,
    tts_id,
    timestamp_ms,
    doc_pack_page,
    doc_pack_char_start,
    doc_pack_char_end,
  }: {
    audio_id: string;
    chapter_id?: string;
    timestamp_ms: number;
    tts_id?: string;
    doc_pack_page?: number;
    doc_pack_char_start?: number;
    doc_pack_char_end?: number;
  }) => {
    await runApiPromise(
      Effect.flatMap(ApiClient, (api) =>
        api.Audio.UpdateAudioProgress({
          params: { audioId: audio_id },
          payload: {
            timestamp_ms: Math.round(timestamp_ms),
            chapter_id: chapter_id ?? null,
            tts_id: tts_id ?? null,
            doc_pack_page: doc_pack_page ?? null,
            doc_pack_char_start: doc_pack_char_start ?? null,
            doc_pack_char_end: doc_pack_char_end ?? null,
          },
        }),
      ).pipe(
        // A progress write is throttled + fire-and-forget: a rejection must never
        // escape as an uncaught promise and crash the reader. Log it instead. The root
        // fix keeps audio.page_count in sync with the pack (materialize-chapters), so a
        // legitimate page is never rejected — a failure here now signals a genuine
        // client/anchor bug worth seeing, not something to clamp/hide.
        Effect.catch((error) =>
          Effect.logWarning('Failed to persist reading progress').pipe(
            Effect.annotateLogs({ error }),
          ),
        ),
      ),
    );
    console.log(
      'Throttled progress updated',
      audio_id,
      chapter_id,
      tts_id,
      doc_pack_page,
      doc_pack_char_start,
      formatTimeSeconds(timestamp_ms / 1000),
    );
  },
  {
    noLeading: true,
  },
);

type LibraryPage = { items: ReadonlyArray<AudioItem>; nextCursor?: string };

// The skip controls / auto-advance need the current audio's neighbor in the
// library list (created_at desc). We seed from the warm library infinite cache
// (route loaders + the nav/library views keep it warm) and then follow the
// cursor on demand — stopping the moment the neighbor is known. The common case
// (a recently-created audio already on page 1) costs zero network; an older
// audio, or a cold cache (e.g. a directly-opened `/app/readalong/$id` link,
// which has no loader to prefetch the library), pages forward until found
// instead of silently failing. This restores the pre-PR skip semantics without
// re-introducing a render-time or 20s-poll full drain.
//
// TODO(scaling): the proper fix is dedicated next/previous-by-id backend
// endpoints so a skip is a single O(1) keyset lookup — see
// docs/projects/backlog/backend/list-and-get-audio-endpoints-v3.md.
async function findAudioNeighbor(
  queryClient: QueryClient,
  audioId: string | undefined,
  offset: 1 | -1,
): Promise<AudioItem | null> {
  const sortMode = getLibrarySortMode();
  const continueItems =
    sortMode === 'continue-first'
      ? AudioItems.selectContinueListening(
          queryClient.getQueryData<LibraryPage>(continueListeningQueryKey)
            ?.items ?? [],
        )
      : [];
  const cached = queryClient.getQueryData<InfiniteData<LibraryPage>>(
    libraryListQueryKey(),
  );
  const items: Array<AudioItem> = cached ? [...flattenAudioPages(cached)] : [];
  let cursor = cached?.pages.at(-1)?.nextCursor;
  // Cold cache → there's always at least a first page left to fetch.
  let hasMore = cached ? cursor !== undefined : true;

  // Resolve the neighbor from the rows loaded so far. `undefined` means "need
  // another page" (only ever returned while `hasMore`), so the loop terminates.
  const resolve = (): AudioItem | null | undefined => {
    const ordered = pinContinueListening(items, sortMode, continueItems);
    if (!audioId) {
      if (ordered.length > 0) return ordered[0];
      return hasMore ? undefined : null;
    }
    const idx = ordered.findIndex((a) => a.id === audioId);
    if (idx === -1) return hasMore ? undefined : null;
    const neighborIdx = idx + offset;
    if (neighborIdx < 0) return null;
    if (neighborIdx < ordered.length) return ordered[neighborIdx];
    return hasMore ? undefined : null;
  };

  let neighbor = resolve();
  while (neighbor === undefined) {
    const page = await runApiPromise(
      libraryListInfiniteArgs().queryFn({ pageParam: cursor }),
    );
    items.push(...page.items);
    cursor = page.nextCursor;
    hasMore = cursor !== undefined;
    neighbor = resolve();
  }
  return neighbor;
}

function findNextAudio({
  queryClient,
  audioId,
}: {
  queryClient: QueryClient;
  audioId?: string;
}) {
  return findAudioNeighbor(queryClient, audioId, 1);
}

function findPreviousAudio({
  queryClient,
  audioId,
}: {
  queryClient: QueryClient;
  audioId?: string;
}) {
  return findAudioNeighbor(queryClient, audioId, -1);
}

function useCurrentChapterSummary() {
  const documentSummary = useDocumentSummary().documentSummary;
  const chapterId = useAudioChunkSelector((s) => s.chapterId);

  return {
    currentChapter: documentSummary?.chapters.find((c) => c.id === chapterId),
    documentSummary,
  };
}

export const audioService = {
  constants: {
    readyAudioStatuses,
  },
  secondaryQueries: {
    useContinueListeningAudioItems,
  },
  mutations: {
    useDeleteAudioMutation,
    useUpdateAudioMutation,
    useCreateAudioMutation,
    useUnarchiveAudioMutation,
  },
  player: {
    postAudioProgressThrottled,
    useLoadAudioFromProgress,
  },
  store: {
    useCurrentAudio,
    useAudioPrefsStore,
    useCurrentChapterSummary,
    useSessionConversionHistoryStore,
  },
  utils: {
    findNextAudio,
    findPreviousAudio,
    processingItemStatusString,
    useMatchIsReadAlongScreenActiveForAudio,
  },
};
